import {
  Alert,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SvgIcon,
  Tooltip,
} from '@mui/material';
import { RiAddLine, RiDeleteBinLine } from '@remixicon/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import InputHeader from '../../../components/InputHeader';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { SimpleContractAssignedVessels } from '../../../types/contract';
import { SelectItem, VesselStatus } from '../../../types/core';
import { MINIMUM_AMOUNT_OF_VESSELS_PER_OPERATOR } from '../../../types/vessel';
import { ContractTemplateSubPageContext } from './types';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';
import useFreshDataEnsurer from '../../../hooks/useFreshDataEnsurer';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

type AssignedVesselsFormValues = {
  primaryVesselId: string | null;
  backupVesselId: string | null;
  [index: string]: string | null;
};

const AssignedVesselsPage = () => {
  const { t } = useLanguage();
  const { getVessels, updateContractAssignedVessels } = useIotsApi();
  const {
    contract,
    isContractDisabled,
    assignedVesselsManagement: { assignedVessels, isFetching, refetch },
  } = useOutletContext<ContractTemplateSubPageContext>();

  useFreshDataEnsurer(isFetching, refetch);

  const {
    data: fetchedVessels = [],
    isFetching: isFetchingVessels,
    error: vesselsError,
  } = useQuery({
    queryKey: ['vessels'],
    queryFn: () => getVessels(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    // Default sorting by name
    select: vessels =>
      [...vessels].sort((a, b) => a.vesselName.localeCompare(b.vesselName)),
  });

  useQueryErrorNotifier([vesselsError]);

  const { allowedVessels, vesselsMap } = useMemo<{
    allowedVessels: SelectItem[];
    vesselsMap: Record<string, SelectItem>;
  }>(() => {
    const allowedVessels: SelectItem[] = [];
    const vesselsMap: Record<string, SelectItem> = {};

    fetchedVessels.forEach(vessel => {
      // Only the vessels related to the contract operator Id are allowed
      // For operator users, this is a no op as the endpoints should have returned the proper data
      if (vessel.operatorId !== contract.operatorId) {
        return;
      }

      const selectItem: SelectItem = {
        value: vessel.id,
        label: `${vessel.vesselName}${vessel.status !== VesselStatus.Compliant ? ` (${t(`vessel-status.${vessel.status}`)})` : ''}`,
      };

      // Add the select item to the map and the list
      vesselsMap[selectItem.value] = selectItem;
      allowedVessels.push(selectItem);
    });

    return { allowedVessels, vesselsMap };
  }, [contract, fetchedVessels, t]);

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const updateContractAssignedVesselsMutation = useMutation({
    mutationFn: (assignedVessels: SimpleContractAssignedVessels) =>
      updateContractAssignedVessels(contract.id, assignedVessels),
  });

  const areThereLessThanTheMinimumVesselsAmount =
    !isFetchingVessels &&
    allowedVessels.length < MINIMUM_AMOUNT_OF_VESSELS_PER_OPERATOR;

  const shouldInputsBeDisabled =
    isFetchingVessels ||
    areThereLessThanTheMinimumVesselsAmount ||
    updateContractAssignedVesselsMutation.isPending ||
    isContractDisabled;

  const initialValues = useMemo<AssignedVesselsFormValues>(() => {
    if (!assignedVessels) {
      return {
        primaryVesselId: null,
        backupVesselId: null,
      };
    }
    const assignedVesselsFormValues: AssignedVesselsFormValues = {
      primaryVesselId: assignedVessels.primary.vesselId,
      backupVesselId: assignedVessels.backups[0]?.vesselId ?? null,
    };

    assignedVessels.backups.slice(1).forEach(({ vesselId }, index) => {
      assignedVesselsFormValues[`backupVesselId_${index}`] = vesselId;
    });

    return assignedVesselsFormValues;
  }, [assignedVessels]);

  const {
    formState,
    materialInputHelpers: { getBaseProps, getSelectProps, renderError },
  } = useIotsAdminForm<AssignedVesselsFormValues>(
    {
      initialValues,
      onSubmit: (values, callback) => {
        const { primaryVesselId, backupVesselId, ...restOfBackupVesselsIds } =
          values;

        const newAssignedVessels: SimpleContractAssignedVessels = {
          primaryId: primaryVesselId as string,
          backupIds: [backupVesselId as string],
        };

        Object.values(restOfBackupVesselsIds).forEach(extraBackupVesselId => {
          newAssignedVessels.backupIds.push(extraBackupVesselId as string);
        });

        updateContractAssignedVesselsMutation.mutate(newAssignedVessels, {
          onSuccess: () => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.contract-vessels-assignment-success'),
            });
            refetch();
            callback();
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(
                error,
                t('content.contract-vessels-assignment-error'),
              ),
            });
            updateContractAssignedVesselsMutation.reset();
          },
        });
      },
      validate: (values, setError) => {
        const uniqueIdsSet = new Set<string>();
        Object.entries(values).forEach(([key, value]) => {
          if (value === null) {
            setError(key, t('validation.required-field'));
          } else if (uniqueIdsSet.has(value)) {
            setError(key, t('validation.duplicated-value'));
          } else {
            uniqueIdsSet.add(value);
          }
        });
      },
      resetOnInitialValuesChange: () => !!assignedVessels,
    },
    {
      shouldInputsBeDisabled,
    },
  );

  const backupVesselsValues: Record<string, string | null> = {
    ...formState.values,
  };
  delete backupVesselsValues.primaryVesselId;

  const backupVesselsKeys = Object.keys(backupVesselsValues);

  const handleAddBackupClick = () => {
    const [largestId] = backupVesselsKeys
      .map(key => Number(key.split('_')[1] ?? 0))
      .sort((a, b) => b - a);
    formState.addFields({ [`backupVesselId_${largestId + 1}`]: null });
  };

  return (
    <CommonMainPanelForm
      onSubmit={formState.handleSubmit}
      isEditing={formState.isEditing}
      onCancelEdition={formState.cancelEdition}
      onEditionRequest={formState.requestEdition}
      isSubmitDisabled={
        shouldInputsBeDisabled ||
        formState.hasVisibleErrors ||
        !formState.haveValuesChanged
      }
      isEditDisabled={shouldInputsBeDisabled}
    >
      <div className="grid grid-rows-[min-content_1fr]">
        <div>
          {areThereLessThanTheMinimumVesselsAmount && (
            <Alert className="mb-6" color="error">
              {t(
                'content.unmet-miminum-vessels-needed-for-contract-assignations',
              )}
            </Alert>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-6">
          <div>
            <label className="mb-4">
              <FormControl error={getBaseProps('primaryVesselId').error}>
                <InputHeader required>{t('content.primary')}</InputHeader>
                <Select<string>
                  {...getSelectProps('primaryVesselId')}
                  displayEmpty
                  renderValue={selected => {
                    if (!selected) {
                      return (
                        <span className="text-iots-grey-400">
                          {t('content.select')}
                        </span>
                      );
                    }
                    return vesselsMap[selected]?.label ?? '-';
                  }}
                >
                  {allowedVessels.map(vessel => (
                    <MenuItem key={vessel.value} value={vessel.value}>
                      {vessel.label}
                    </MenuItem>
                  ))}
                </Select>
                {renderError('primaryVesselId')}
              </FormControl>
            </label>
          </div>
          <div>
            <InputHeader required>{t('content.backup')}</InputHeader>
            {backupVesselsKeys.map(backupVesselKey => (
              <label key={backupVesselKey} className="block mb-4">
                <FormControl error={getBaseProps(backupVesselKey).error}>
                  <div className="grid grid-cols-[1fr_min-content] items-center">
                    <Select<string>
                      {...getSelectProps(backupVesselKey)}
                      displayEmpty
                      renderValue={selected => {
                        if (!selected) {
                          return (
                            <span className="text-iots-grey-400">
                              {t('content.select')}
                            </span>
                          );
                        }
                        return vesselsMap[selected]?.label ?? '-';
                      }}
                    >
                      {allowedVessels.map(vessel => (
                        <MenuItem key={vessel.value} value={vessel.value}>
                          {vessel.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formState.isEditing &&
                      backupVesselKey !== 'backupVesselId' && (
                        <div>
                          <Tooltip title={t(`content.delete`)}>
                            <IconButton
                              onClick={() => {
                                formState.removeFields(backupVesselKey);
                              }}
                              disabled={getBaseProps(backupVesselKey).disabled}
                            >
                              <SvgIcon component={RiDeleteBinLine} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                  </div>
                  {renderError(backupVesselKey)}
                </FormControl>
              </label>
            ))}
            {formState.isEditing && !shouldInputsBeDisabled && (
              <Button
                startIcon={<SvgIcon component={RiAddLine} />}
                size="small"
                onClick={handleAddBackupClick}
              >
                {t('content.add')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </CommonMainPanelForm>
  );
};

export default AssignedVesselsPage;
